const NOISE_FPS = 8;
const NOISE_OPACITY_BASE = 0.03;
const NOISE_OPACITY_BURST = 0.10;
const BURST_INTERVAL_MIN = 5000;
const BURST_INTERVAL_MAX = 8000;
const BURST_DURATION = 200;
const BAND_COUNT = 3;
const BAND_HEIGHT = 16;
const BAND_DRIFT_SPEED = 0.3;
const JITTER_MAX = 3;

export class VhsNoise {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width = 256;
  private height = 256;
  private animId: number | null = null;
  private lastFrame = 0;
  private frameInterval: number;
  private isBurst = false;
  private burstTimeout: number | null = null;
  private bands: number[] = [];

  constructor(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas.className = 'vhs-noise-canvas';
    container.appendChild(this.canvas);

    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;
    this.frameInterval = 1000 / NOISE_FPS;

    for (let i = 0; i < BAND_COUNT; i++) {
      this.bands.push(Math.random() * this.height);
    }

    this.scheduleBurst();
    this.loop(0);
  }

  private loop(time: number): void {
    this.animId = requestAnimationFrame((t) => this.loop(t));

    if (time - this.lastFrame < this.frameInterval) return;
    this.lastFrame = time;

    this.updateBands();
    this.render();
  }

  private updateBands(): void {
    for (let i = 0; i < this.bands.length; i++) {
      this.bands[i] += BAND_DRIFT_SPEED;
      if (this.bands[i] > this.height) {
        this.bands[i] = -BAND_HEIGHT;
      }
    }
  }

  private render(): void {
    const { ctx, width, height } = this;
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    const opacity = this.isBurst ? NOISE_OPACITY_BURST : NOISE_OPACITY_BASE;
    const alpha = Math.round(opacity * 255);

    for (let y = 0; y < height; y++) {
      const inBand = this.isInBand(y);
      const bandAlpha = inBand ? Math.round(alpha * 3) : alpha;
      const jitter = this.isBurst && inBand ? Math.floor(Math.random() * JITTER_MAX * 2) - JITTER_MAX : 0;

      for (let x = 0; x < width; x++) {
        const srcX = x + jitter;
        if (srcX < 0 || srcX >= width) continue;

        const idx = (y * width + x) * 4;
        const v = Math.random() * 255;
        data[idx] = v;
        data[idx + 1] = v;
        data[idx + 2] = v;
        data[idx + 3] = bandAlpha;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  private isInBand(y: number): boolean {
    for (const bandY of this.bands) {
      if (y >= bandY && y < bandY + BAND_HEIGHT) return true;
    }
    return false;
  }

  private scheduleBurst(): void {
    const delay = BURST_INTERVAL_MIN + Math.random() * (BURST_INTERVAL_MAX - BURST_INTERVAL_MIN);
    this.burstTimeout = window.setTimeout(() => {
      this.isBurst = true;
      window.setTimeout(() => {
        this.isBurst = false;
        this.scheduleBurst();
      }, BURST_DURATION);
    }, delay);
  }

  dispose(): void {
    if (this.animId) cancelAnimationFrame(this.animId);
    if (this.burstTimeout) clearTimeout(this.burstTimeout);
    this.canvas.remove();
  }
}
